import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { MaterialItem } from '../../domain/procurementRequest';
import type { RfqVendorInvitationRow } from '../../domain/types';
import { RfqVendorColumnOrderStrip } from './RfqVendorColumnOrderStrip';
import { RfqVendorQuotesMaterialTables } from './RfqVendorQuotesTable';
import {
  defaultVendorColumnOrder,
  mergeVendorOrder,
  readSavedVendorOrder,
  vendorQuoteOrderStorageKey,
  writeSavedVendorOrder
} from './rfqVendorQuotesOrder';

export interface RfqVendorQuotesComparisonProps {
  rfqId: string;
  rfqNumber: string;
  materials: MaterialItem[];
  vendors: RfqVendorInvitationRow[];
  onOpenAttachment: (vendorId: string, fileName: string) => void;
  readOnly?: boolean;
  /** After operations approval — drag rank is disabled (still view quotes / PDFs). */
  lockRecommendationOrder?: boolean;
  /** When set, strip shows this vendor first (e.g. operations-approved vendor). */
  pinVendorIdFirst?: string | null;
}

function pinVendorFirstIfPresent(order: string[], pinId: string | null | undefined): string[] {
  const pin = pinId?.trim();
  if (!pin || !order.includes(pin)) {
    return order;
  }
  return [pin, ...order.filter((id) => id !== pin)];
}

export function RfqVendorQuotesComparison({
  rfqId,
  rfqNumber,
  materials,
  vendors,
  onOpenAttachment,
  readOnly = false,
  lockRecommendationOrder = false,
  pinVendorIdFirst = null
}: RfqVendorQuotesComparisonProps): ReactElement {
  const invited = useMemo(() => vendors.filter((v) => v.vendorName?.trim()), [vendors]);

  const storageKey = useMemo(() => vendorQuoteOrderStorageKey(rfqId), [rfqId]);

  const defaultOrder = useMemo(() => defaultVendorColumnOrder(invited), [invited]);

  const invitedKey = useMemo(() => invited.map((v) => v.vendorId).sort().join('|'), [invited]);

  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  useEffect(() => {
    const saved = readSavedVendorOrder(storageKey);
    const currentIds = invited.map((v) => v.vendorId);
    let merged = mergeVendorOrder(saved, currentIds, defaultOrder);
    merged = pinVendorFirstIfPresent(merged, pinVendorIdFirst);
    setOrderedIds(merged);
  }, [rfqId, invitedKey, storageKey, defaultOrder, invited, pinVendorIdFirst]);

  const orderedInvited = useMemo(() => {
    const byId = new Map(invited.map((v) => [v.vendorId, v] as const));
    return orderedIds.map((id) => byId.get(id)).filter((v): v is RfqVendorInvitationRow => v != null);
  }, [invited, orderedIds]);

  const stripReadOnly = readOnly || lockRecommendationOrder;

  const handleReorder = useCallback(
    (nextIds: string[]) => {
      if (stripReadOnly) {
        return;
      }
      setOrderedIds(nextIds);
      writeSavedVendorOrder(storageKey, nextIds);
    },
    [storageKey, stripReadOnly]
  );

  return (
    <div className="rfq-vendor-quotes-comparison full-width">
      <h5 className="po-subheading rfq-comparison-title">Vendor quotes — {rfqNumber}</h5>
      <p className="hint">Side-by-side comparison for tubular and string design lines.</p>

      {invited.length > 0 ? (
        <RfqVendorColumnOrderStrip
          orderedInvited={invited}
          orderedIds={orderedIds}
          onReorder={handleReorder}
          readOnly={stripReadOnly}
        />
      ) : null}

      <RfqVendorQuotesMaterialTables
        materials={materials}
        orderedInvited={orderedInvited}
        onOpenAttachment={onOpenAttachment}
      />
    </div>
  );
}
